import i18n from 'i18next';

import { minutesToHours } from '@/helpers/date-format';
import { ConflictFieldConfig } from '@/modules/projects/types/project-conflicts';

/**
 * Field keys for team member capacity conflict resolution
 * Uses form field names as keys for consistency with the form
 */
export type TeamMemberCapacityFieldKey =
  | 'isOnDemandAvailability'
  | 'capacityPerDay'
  | 'billableCapacity'
  | 'corporateFunctionCapacity';

/**
 * Field configurations for team member capacity conflict resolution
 * Defines how each field should be displayed in the conflicts table
 */
export const teamMemberCapacityConflictFields: ConflictFieldConfig<TeamMemberCapacityFieldKey>[] = [
  {
    key: 'isOnDemandAvailability',
    snapshotKey: 'isOnDemand',
    labelKey: 'conflictFields.capacity.onDemand',
    valueFormatter: (value: boolean | null | undefined) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      return value
        ? i18n.t('authenticated.team:valueDisplay.yes')
        : i18n.t('authenticated.team:valueDisplay.no');
    },
  },
  {
    key: 'capacityPerDay',
    snapshotKey: 'totalWorkingMinutes',
    labelKey: 'conflictFields.capacity.hoursPerDay',
    // Form has hours, snapshot has minutes
    getFormValueForComparison: (formValue: number | null | undefined) => {
      return formValue ?? null;
    },
    getSnapshotValueForComparison: (snapshotValue: number | null | undefined) => {
      if (snapshotValue === null || snapshotValue === undefined) return null;
      return minutesToHours(snapshotValue);
    },
    valueFormatter: (value: number | null | undefined) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      // Value might be in minutes (from snapshot) or hours (from form)
      // We need to display in hours
      const hours = value > 24 ? minutesToHours(value) : value;
      return `${hours}h`;
    },
    // Disabled when On Demand is selected — capacity fields are irrelevant for on-demand members
    isDisabled: (selectedValues: Record<string, any>) =>
      selectedValues.isOnDemandAvailability === true,
  },
  {
    key: 'billableCapacity',
    labelKey: 'conflictFields.capacity.billableCapacity',
    valueFormatter: (value: number | null | undefined) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      // Round to integer for display; backend receives the full float value
      return `${Math.round(value)}%`;
    },
    // Disabled when On Demand is selected — capacity fields are irrelevant for on-demand members
    isDisabled: (selectedValues: Record<string, any>) =>
      selectedValues.isOnDemandAvailability === true,
  },
  {
    key: 'corporateFunctionCapacity',
    labelKey: 'conflictFields.capacity.corporateFunctionCapacity',
    // This is a derived field: 100 - billableCapacity
    // It doesn't exist in snapshot, need to calculate it
    snapshotKey: 'billableCapacity',
    // Convert snapshot's billableCapacity to corporateFunctionCapacity for display and comparison.
    // Applied first in the pipeline; getSnapshotValueForComparison is intentionally omitted
    // to avoid double-transforming (normalizeSnapshotValue already produces the corporate value).
    normalizeSnapshotValue: (snapshotValue: number | null | undefined) => {
      if (snapshotValue === null || snapshotValue === undefined) return null;
      return 100 - snapshotValue;
    },
    getFormValueForComparison: (formValue: number | null | undefined) => {
      return formValue ?? null;
    },
    valueFormatter: (value: number | null | undefined) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      // Use complementary rounding: 100 - round(billableCapacity) to guarantee the two values sum to 100%.
      // Since value = 100 - billableCapacity, billableCapacity = 100 - value.
      return `${100 - Math.round(100 - value)}%`;
    },
    // Disabled when On Demand is selected — capacity fields are irrelevant for on-demand members
    isDisabled: (selectedValues: Record<string, any>) =>
      selectedValues.isOnDemandAvailability === true,
  },
];
